/**
 * Colour tokens, colour references and colour maths (plan §7.3, D9). Split from `theme.ts` so the
 * block registry and code elements can use colours without the theme schemas: the block validator
 * is part of every bundle that validates a document, the theme model is not.
 *
 * Owner: doc-engine (wave 1), design-system (wave 2).
 */
import { z } from 'zod';

// -------------------------------------------------------------------------------------------------
// Colours
// -------------------------------------------------------------------------------------------------

export const HEX_RE = /^#[0-9a-fA-F]{6}$/;
export const Hex = z.string().regex(HEX_RE, { message: 'Use a colour such as #444CE7' });
export type Hex = z.infer<typeof Hex>;

/** The seven colour roles every theme defines. */
export const THEME_COLOR_ROLES = ['background', 'surface', 'ink', 'muted', 'accent', 'soft', 'dark'] as const;
export type ThemeColorRole = (typeof THEME_COLOR_ROLES)[number];

/**
 * Named colours for text and block highlights (the page editor's colour menu). Each theme resolves
 * them for text and for backgrounds, so a page stays readable on light and dark themes.
 */
export const NAMED_COLORS = [
  'gray',
  'brown',
  'orange',
  'yellow',
  'green',
  'blue',
  'purple',
  'pink',
  'red',
] as const;
export type NamedColor = (typeof NAMED_COLORS)[number];

/** At most eight brand colours (`brand.0` … `brand.7`). */
export const MAX_BRAND_COLORS = 8;
const BRAND_TOKEN_RE = /^brand\.[0-7]$/;

export type ColorToken = ThemeColorRole | NamedColor | `brand.${number}`;
export const ColorToken = z.union([
  z.enum(THEME_COLOR_ROLES),
  z.enum(NAMED_COLORS),
  z
    .string()
    .regex(BRAND_TOKEN_RE, { message: 'Brand colours are brand.0 to brand.7' })
    .transform((v) => v as `brand.${number}`),
]);

export const ColorRef = z.union([z.object({ token: ColorToken }).strict(), z.object({ hex: Hex }).strict()]);
export type ColorRef = { token: ColorToken } | { hex: string };

export const isTokenColor = (ref: ColorRef): ref is { token: ColorToken } => 'token' in ref;

/** Horizontal alignment of text (blocks, text styles). */
export const TextAlign = z.enum(['left', 'center', 'right', 'justify']);
export type TextAlign = z.infer<typeof TextAlign>;

// -------------------------------------------------------------------------------------------------
// Colour maths (contrast and nearest token)
// -------------------------------------------------------------------------------------------------

function channels(hex: string): [number, number, number] {
  return [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16) / 255) as [number, number, number];
}

export function relativeLuminance(hex: string): number {
  const [r, g, b] = channels(hex).map((v) => (v <= 0.04045 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4)) as [
    number,
    number,
    number,
  ];
  return r * 0.2126 + g * 0.7152 + b * 0.0722;
}

/** WCAG contrast ratio of two hex colours (1 … 21). */
export function contrastRatio(a: string, b: string): number {
  const x = relativeLuminance(a),
    y = relativeLuminance(b);
  return (Math.max(x, y) + 0.05) / (Math.min(x, y) + 0.05);
}

function hsl(hex: string): { h: number; s: number; l: number } {
  const [r, g, b] = channels(hex);
  const max = Math.max(r, g, b),
    min = Math.min(r, g, b),
    l = (max + min) / 2;
  if (max === min) return { h: 0, s: 0, l };
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  const h =
    max === r
      ? ((g - b) / d + (g < b ? 6 : 0)) * 60
      : max === g
        ? ((b - r) / d + 2) * 60
        : ((r - g) / d + 4) * 60;
  return { h, s, l };
}

/**
 * The named colour closest to an arbitrary CSS colour (used when pasting from Word, Google Docs or
 * the web). Returns `null` for near-black, near-white and unsaturated dark greys, which render as
 * the default text colour.
 */
export function nearestNamedColor(hex: string): NamedColor | null {
  if (!HEX_RE.test(hex)) return null;
  const { h, s, l } = hsl(hex);
  if (l < 0.18 || l > 0.94) return null;
  if (s < 0.18) return l < 0.3 ? null : 'gray';
  if ((h < 40 || h >= 345) && l < 0.42 && s < 0.65 && h >= 10) return 'brown';
  if (h >= 345 || h < 15) return 'red';
  if (h < 40) return 'orange';
  if (h < 65) return 'yellow';
  if (h < 165) return 'green';
  if (h < 250) return 'blue';
  if (h < 295) return 'purple';
  return 'pink';
}

/** Parse `#abc`, `#aabbcc`, `rgb(r, g, b)` and a few CSS names into `#RRGGBB`, or null. */
export function cssColorToHex(value: string): string | null {
  const v = value.trim().toLowerCase();
  const names: Record<string, string> = {
    black: '#000000',
    white: '#FFFFFF',
    red: '#FF0000',
    green: '#008000',
    blue: '#0000FF',
    yellow: '#FFFF00',
    orange: '#FFA500',
    purple: '#800080',
    gray: '#808080',
    grey: '#808080',
    brown: '#A52A2A',
    pink: '#FFC0CB',
  };
  if (names[v]) return names[v]!;
  let m = /^#([0-9a-f])([0-9a-f])([0-9a-f])$/.exec(v);
  if (m) return `#${m[1]}${m[1]}${m[2]}${m[2]}${m[3]}${m[3]}`.toUpperCase();
  m = /^#([0-9a-f]{6})$/.exec(v);
  if (m) return `#${m[1]}`.toUpperCase();
  m = /^rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*(?:,\s*([\d.]+)\s*)?\)$/.exec(v);
  if (m) {
    if (m[4] !== undefined && Number(m[4]) === 0) return null;
    const hex = [m[1], m[2], m[3]].map((n) => Math.min(255, Number(n)).toString(16).padStart(2, '0'));
    return `#${hex.join('')}`.toUpperCase();
  }
  return null;
}
